import { FeatureFlag, FeatureFlags, useFeatureFlags } from "@me8eon/featureflags";
import { GetterSetter, makeContextFor } from "@me8eon/context";
import React, { FC, ReactNode, useMemo } from "react";
import { SelectableButton } from "./selectable.button";
import { Table } from "./table";
import { LoadingOrErrorProps } from "@me8eon/async/src/simple.loading.or.error";
import listItem from "me-at-eon/src/components/search-list/ListItem";

export type MonoClineTc<Item> = {
    categorise: (item: Item) => string;
};
export type MonoclineCategoryProps<Item> = {
    index: number
    selected?: boolean;
    category: string;
    onClick?: (category: string, index: number) => void;
    isMobile?: boolean;
}
export type MonoclineDataProps<Item> = {
    item: Item;
    index: number
    selected?: boolean;
    category: string;
    onClick?: (item: Item, index: number) => void;
    isMobile?: boolean;
}
export type MonoClineProps<Item> = {
    items: Item[];
    typeClass: MonoClineTc<Item>;
    Header: FC<MonoclineCategoryProps<Item>>;
    Data: FC<MonoclineDataProps<Item>>;
    selectedItem?: Item
    onCategoryClick?: (category: string, index: number) => void;
    onDataClick?: (item: Item, index: number) => void;
    disclaimer?: ReactNode;
    isMobile?: boolean;
};

export type MonoCline<Item> = FC<MonoClineProps<Item>>

export interface ClipHeightProps {
    children: ReactNode;
    maxHeight: string;  // e.g., "200px", "50vh"
    scrollable?: boolean;
    force?: boolean;  // Force height if true
}

export type ClipHeight = (props: ClipHeightProps) => ReactNode;

export type SelectableButtonBarProps = {
    options: string[],
    selectedOps: GetterSetter<string>,
    rootId: string
};
export type SelectableButtonBar = (props: SelectableButtonBarProps) => React.ReactNode;

export interface MouseOverProps {
    children: ReactNode;
    linkHover?: string;
    style?: React.CSSProperties;
    changeColour?: (colour: string) => void;
}

export type MouseOver = (props: MouseOverProps) => ReactNode;

export interface BgMouseOverProps {
    children: ReactNode;
    bgHover?: string;
    changeColour?: (colour: string) => void;
}

export type BgMouseOver = (props: BgMouseOverProps) => ReactNode;


export type TwoColumnAndRestLayoutProps = {
    rootId?: string;
    children: React.ReactNode;
};

export type TwoColumnAndRestLayout = (props: TwoColumnAndRestLayoutProps) => React.ReactElement;

export interface OutsideClickProps {
    onClickOutside: () => void;
    children: ReactNode;
}

export type OutsideClick = (props: OutsideClickProps) => React.ReactNode;
export type CommonComponents = {
    SelectableButton: SelectableButton
    ClipHeight: ClipHeight
    Table: Table
    OutsideClick: OutsideClick
    MouseOver: MouseOver,
    BgMouseOver: BgMouseOver,
    TwoColumnAndRest: TwoColumnAndRestLayout
    SelectableButtonBar: SelectableButtonBar
    LoadingOrError: FC<LoadingOrErrorProps<any>>
    MonoCline: MonoCline<any>
}
export type CommonComponentsFn = (ffs: FeatureFlags) => CommonComponents

export function useLoadingOrError<T>(): FC<LoadingOrErrorProps<T>> {
    const { LoadingOrError } = useCommonComponents();
    return LoadingOrError;
}

export type CommonComponentsProviderProps = {
    componentsFn: CommonComponentsFn
    children: React.ReactNode
}

export const { use: useCommonComponents, Provider: RawCommonComponentsProvider } = makeContextFor<CommonComponents, "commonComponents">("commonComponents");

export function CommonComponentsProvider({ componentsFn, children }: CommonComponentsProviderProps) {
    const [ffs] = useFeatureFlags();
    const cc = useMemo(() => componentsFn(ffs), [ffs]);
    return <RawCommonComponentsProvider commonComponents={cc}> {children} </RawCommonComponentsProvider>;
}

